(function () {
  const SELECTORS = {
    root: '[data-ehua-guided-collection]',
    card: '[data-ehua-product-card]',
    chip: '[data-filter-group][data-filter-value]',
    reset: '[data-ehua-reset-filters]',
    selectedSummary: '[data-ehua-selected-summary]',
    matchCount: '[data-ehua-match-count]',
    gridCount: '[data-ehua-grid-count]',
    recommendation: '[data-ehua-recommendation]',
    recommendationMedia: '[data-ehua-recommendation-media]',
    recommendationTitle: '[data-ehua-recommendation-title]',
    recommendationProduct: '[data-ehua-recommendation-product]',
    recommendationBody: '[data-ehua-recommendation-body]',
    recommendationReasons: '[data-ehua-recommendation-reasons]',
    recommendationLink: '[data-ehua-recommendation-link]',
    recommendationNav: '[data-ehua-recommendation-nav]',
    recommendationPrevious: '[data-ehua-recommendation-previous]',
    recommendationNext: '[data-ehua-recommendation-next]',
    recommendationPosition: '[data-ehua-recommendation-position]',
    viewMatches: '[data-ehua-view-matches]',
    gridView: '[data-ehua-grid-view]',
    gridReset: '[data-ehua-grid-reset]',
  };

  const ROOT_STATE = new WeakMap();

  const REASONS = {
    width: 'Uses the selected strap width.',
    attachment: 'Matches the selected attachment behavior.',
    form: 'Matches the selected collection route.',
    safety: 'Matches the selected safety breakaway preference.',
    release: 'Matches the selected release style.',
  };

  function normalize(value) {
    return String(value || '').trim();
  }

  function labelForCount(count) {
    if (count === 0) return 'No exact match';
    return count === 1 ? '1 match' : `${count} matches`;
  }

  function cardMatches(card, state, skipGroup) {
    return Object.keys(state).every((group) => {
      if (group === skipGroup) return true;
      return normalize(card.dataset[group]) === state[group];
    });
  }

  function getSelectedState(chips) {
    const state = {};
    chips.forEach((chip) => {
      if (chip.classList.contains('is-selected')) {
        state[chip.dataset.filterGroup] = normalize(chip.dataset.filterValue);
      }
    });
    return state;
  }

  function setText(node, value) {
    if (node) node.textContent = value;
  }

  function getRootState(root) {
    if (!ROOT_STATE.has(root)) {
      ROOT_STATE.set(root, {
        recommendationIndex: 0,
        recommendationSignature: '',
        viewMode: 'context',
        animationTimer: null,
      });
    }

    return ROOT_STATE.get(root);
  }

  function supportsGlassSurfaceFilter(filterId) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;

    const isWebkit = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isFirefox = /Firefox/.test(navigator.userAgent);

    if (isWebkit || isFirefox) return false;

    const div = document.createElement('div');
    div.style.backdropFilter = `url(#${filterId})`;

    return div.style.backdropFilter !== '';
  }

  function generateGlassDisplacementMap({
    actualWidth,
    actualHeight,
    borderRadius,
    borderWidth = 0.07,
    brightness = 50,
    opacity = 0.93,
    blur = 11,
    mixBlendMode = 'difference',
    redGradId,
    blueGradId,
  }) {
    const edgeSize = Math.min(actualWidth, actualHeight) * (borderWidth * 0.5);

    const svgContent = `
      <svg viewBox="0 0 ${actualWidth} ${actualHeight}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="${redGradId}" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="red"/>
          </linearGradient>
          <linearGradient id="${blueGradId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="blue"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" fill="black"></rect>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#${redGradId})" />
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#${blueGradId})" style="mix-blend-mode: ${mixBlendMode}" />
        <rect x="${edgeSize}" y="${edgeSize}" width="${actualWidth - edgeSize * 2}" height="${actualHeight - edgeSize * 2}" rx="${borderRadius}" fill="hsl(0 0% ${brightness}% / ${opacity})" style="filter:blur(${blur}px)" />
      </svg>
    `;

    return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
  }

  function createSvgElement(tagName) {
    return document.createElementNS('http://www.w3.org/2000/svg', tagName);
  }

  function initRecommendationMagnifier(media, img) {
    if (!media || !img || window.matchMedia('(hover: none), (pointer: coarse)').matches) return;

    const uniqueId = `recommendation-${Math.random().toString(36).slice(2, 10)}`;
    const filterId = `glass-filter-${uniqueId}`;
    const redGradId = `red-grad-${uniqueId}`;
    const blueGradId = `blue-grad-${uniqueId}`;
    const lensSize = 264;
    const zoom = 2.7;
    const borderRadius = lensSize / 2;

    const lens = document.createElement('div');
    lens.className = `ehua-guided-collection__magnifier glass-surface ${supportsGlassSurfaceFilter(filterId) ? 'glass-surface--svg' : 'glass-surface--fallback'}`;
    lens.style.setProperty('--filter-id', `url(#${filterId})`);
    lens.style.setProperty('--glass-frost', '0.08');
    lens.style.setProperty('--glass-saturation', '1.35');
    lens.setAttribute('aria-hidden', 'true');

    const svg = createSvgElement('svg');
    svg.setAttribute('class', 'glass-surface__filter');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    const defs = createSvgElement('defs');
    const filter = createSvgElement('filter');
    filter.setAttribute('id', filterId);
    filter.setAttribute('color-interpolation-filters', 'sRGB');
    filter.setAttribute('x', '0%');
    filter.setAttribute('y', '0%');
    filter.setAttribute('width', '100%');
    filter.setAttribute('height', '100%');

    const feImage = createSvgElement('feImage');
    feImage.setAttribute('x', '0');
    feImage.setAttribute('y', '0');
    feImage.setAttribute('width', '100%');
    feImage.setAttribute('height', '100%');
    feImage.setAttribute('preserveAspectRatio', 'none');
    feImage.setAttribute('result', 'map');

    const redDisplacement = createSvgElement('feDisplacementMap');
    redDisplacement.setAttribute('in', 'SourceGraphic');
    redDisplacement.setAttribute('in2', 'map');
    redDisplacement.setAttribute('result', 'dispRed');
    redDisplacement.setAttribute('scale', '-180');
    redDisplacement.setAttribute('xChannelSelector', 'R');
    redDisplacement.setAttribute('yChannelSelector', 'G');

    const redMatrix = createSvgElement('feColorMatrix');
    redMatrix.setAttribute('in', 'dispRed');
    redMatrix.setAttribute('type', 'matrix');
    redMatrix.setAttribute('values', '1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0');
    redMatrix.setAttribute('result', 'red');

    const greenDisplacement = createSvgElement('feDisplacementMap');
    greenDisplacement.setAttribute('in', 'SourceGraphic');
    greenDisplacement.setAttribute('in2', 'map');
    greenDisplacement.setAttribute('result', 'dispGreen');
    greenDisplacement.setAttribute('scale', '-170');
    greenDisplacement.setAttribute('xChannelSelector', 'R');
    greenDisplacement.setAttribute('yChannelSelector', 'G');

    const greenMatrix = createSvgElement('feColorMatrix');
    greenMatrix.setAttribute('in', 'dispGreen');
    greenMatrix.setAttribute('type', 'matrix');
    greenMatrix.setAttribute('values', '0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0');
    greenMatrix.setAttribute('result', 'green');

    const blueDisplacement = createSvgElement('feDisplacementMap');
    blueDisplacement.setAttribute('in', 'SourceGraphic');
    blueDisplacement.setAttribute('in2', 'map');
    blueDisplacement.setAttribute('result', 'dispBlue');
    blueDisplacement.setAttribute('scale', '-160');
    blueDisplacement.setAttribute('xChannelSelector', 'R');
    blueDisplacement.setAttribute('yChannelSelector', 'G');

    const blueMatrix = createSvgElement('feColorMatrix');
    blueMatrix.setAttribute('in', 'dispBlue');
    blueMatrix.setAttribute('type', 'matrix');
    blueMatrix.setAttribute('values', '0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0');
    blueMatrix.setAttribute('result', 'blue');

    const redGreenBlend = createSvgElement('feBlend');
    redGreenBlend.setAttribute('in', 'red');
    redGreenBlend.setAttribute('in2', 'green');
    redGreenBlend.setAttribute('mode', 'screen');
    redGreenBlend.setAttribute('result', 'rg');

    const outputBlend = createSvgElement('feBlend');
    outputBlend.setAttribute('in', 'rg');
    outputBlend.setAttribute('in2', 'blue');
    outputBlend.setAttribute('mode', 'screen');
    outputBlend.setAttribute('result', 'output');

    const gaussianBlur = createSvgElement('feGaussianBlur');
    gaussianBlur.setAttribute('in', 'output');
    gaussianBlur.setAttribute('stdDeviation', '0');

    filter.append(feImage, redDisplacement, redMatrix, greenDisplacement, greenMatrix, blueDisplacement, blueMatrix, redGreenBlend, outputBlend, gaussianBlur);
    defs.appendChild(filter);
    svg.appendChild(defs);

    const content = document.createElement('div');
    content.className = 'glass-surface__content';

    const zoomLayer = document.createElement('div');
    zoomLayer.className = 'ehua-guided-collection__magnifier-zoom';

    const zoomImage = document.createElement('img');
    zoomImage.className = 'ehua-guided-collection__magnifier-image';
    zoomImage.src = img.currentSrc || img.src;
    zoomImage.alt = '';
    zoomImage.draggable = false;
    zoomImage.setAttribute('aria-hidden', 'true');

    const zoomGlass = document.createElement('div');
    zoomGlass.className = 'ehua-guided-collection__magnifier-glass';
    zoomGlass.style.setProperty('--filter-id', `url(#${filterId})`);
    zoomGlass.setAttribute('aria-hidden', 'true');

    zoomLayer.append(zoomImage, zoomGlass);
    content.appendChild(zoomLayer);

    lens.append(svg, content);
    media.appendChild(lens);

    const updateDisplacementMap = () => {
      feImage.setAttribute('href', generateGlassDisplacementMap({
        actualWidth: lensSize,
        actualHeight: lensSize,
        borderRadius,
        redGradId,
        blueGradId,
      }));
    };

    const updateLens = (event) => {
      const rect = media.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const visible = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;

      lens.classList.toggle('is-visible', visible);
      if (!visible) return;

      lens.style.transform = `translate3d(${x - lensSize / 2}px, ${y - lensSize / 2}px, 0)`;
      zoomImage.style.width = `${rect.width}px`;
      zoomImage.style.height = `${rect.height}px`;
      zoomImage.style.left = `${lensSize / 2 - x}px`;
      zoomImage.style.top = `${lensSize / 2 - y}px`;
      zoomImage.style.transformOrigin = `${x}px ${y}px`;
      zoomImage.style.transform = `scale(${zoom})`;
    };

    updateDisplacementMap();
    media.addEventListener('pointerenter', updateLens);
    media.addEventListener('pointermove', updateLens);
    media.addEventListener('pointerleave', () => lens.classList.remove('is-visible'));

    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        window.setTimeout(updateDisplacementMap, 0);
      });
      resizeObserver.observe(lens);
    }
  }

  function animateRecommendation(recommendation, direction, rootState) {
    if (!recommendation || !direction) return;

    recommendation.classList.remove('is-swapping-next', 'is-swapping-previous');
    window.clearTimeout(rootState.animationTimer);
    void recommendation.offsetWidth;
    recommendation.classList.add(direction > 0 ? 'is-swapping-next' : 'is-swapping-previous');
    rootState.animationTimer = window.setTimeout(() => {
      recommendation.classList.remove('is-swapping-next', 'is-swapping-previous');
    }, 360);
  }

  function updateRecommendation(root, cards, matches, state, options = {}) {
    const recommendation = root.querySelector(SELECTORS.recommendation);
    const media = root.querySelector(SELECTORS.recommendationMedia);
    const title = root.querySelector(SELECTORS.recommendationTitle);
    const product = root.querySelector(SELECTORS.recommendationProduct);
    const body = root.querySelector(SELECTORS.recommendationBody);
    const reasons = root.querySelector(SELECTORS.recommendationReasons);
    const link = root.querySelector(SELECTORS.recommendationLink);
    const nav = root.querySelector(SELECTORS.recommendationNav);
    const position = root.querySelector(SELECTORS.recommendationPosition);
    const viewMatches = root.querySelector(SELECTORS.viewMatches);
    const rootState = getRootState(root);
    const selectedCount = Object.keys(state).length;
    const signature = `${JSON.stringify(state)}::${matches.map((card) => normalize(card.dataset.sku || card.dataset.url)).join('|')}`;

    if (!options.preserveIndex && rootState.recommendationSignature !== signature) {
      rootState.recommendationIndex = 0;
    }

    rootState.recommendationSignature = signature;
    if (matches.length) {
      rootState.recommendationIndex = Math.min(rootState.recommendationIndex, matches.length - 1);
    } else {
      rootState.recommendationIndex = 0;
    }

    const lead = matches[rootState.recommendationIndex] || cards[0];

    cards.forEach((card) => card.classList.remove('is-recommended'));
    if (!options.direction) {
      recommendation.classList.add('is-updating');
      window.setTimeout(() => recommendation.classList.remove('is-updating'), 280);
    }

    if (media) {
      media.innerHTML = '';
      media.classList.remove('has-image');
    }

    if (nav) nav.hidden = true;
    if (viewMatches) viewMatches.hidden = true;
    setText(position, '1 / 1');
    if (reasons) reasons.innerHTML = '';
    if (product) {
      product.textContent = '';
      product.hidden = true;
    }

    if (!selectedCount) {
      setText(title, 'Choose a few details to surface the best match.');
      setText(body, 'Pick a width, safety option, or release style and the selector will highlight the product that best matches your current setup.');
      if (link) {
        link.href = '/pages/contact';
        link.textContent = 'Ask Us to Review';
      }
      return;
    }

    if (!matches.length) {
      setText(title, 'No exact match yet.');
      setText(body, 'Clear one choice or send us the badge setup. We can review the route and recommend the closest custom lanyard.');
      if (link) {
        link.href = '/pages/contact';
        link.textContent = 'Ask Us to Review';
      }
      return;
    }

    if (lead) {
      lead.classList.add('is-recommended');
      if (product) {
        const sku = normalize(lead.dataset.sku);
        const name = normalize(lead.dataset.title);
        product.textContent = [sku, name].filter(Boolean).join(' / ');
        product.hidden = !product.textContent;
      }
      if (media && lead.dataset.image) {
        const img = document.createElement('img');
        img.src = lead.dataset.image;
        img.alt = lead.dataset.title || 'Recommended Ehua Lanyard';
        img.loading = 'lazy';
        media.appendChild(img);
        initRecommendationMagnifier(media, img);

        if (lead.dataset.priceLabel && lead.dataset.priceValue) {
          const price = document.createElement('span');
          const label = document.createElement('span');
          const value = document.createElement('strong');

          price.className = 'ehua-guided-collection__price-chip ehua-guided-collection__recommendation-price';
          label.textContent = `${lead.dataset.priceLabel} `;
          value.textContent = lead.dataset.priceValue;
          price.append(label, value);
          media.appendChild(price);
        }

        media.classList.add('has-image');
      }
    }

    if (matches.length === 1) {
      setText(title, 'This lanyard is the most likely fit for your use case.');
      setText(body, 'Your selected route and filters point to this SKU as the cleanest match. Review the details, then send artwork, quantity, badge type, deadline, and shipping destination for a quote.');
    } else {
      setText(title, 'Several good fits are still available.');
      setText(body, 'Use the arrows to compare every current match, or choose one more detail to narrow the recommendation.');
      if (nav) nav.hidden = false;
      if (viewMatches) viewMatches.hidden = false;
      setText(position, `${rootState.recommendationIndex + 1} / ${matches.length}`);
    }

    if (link && lead) {
      link.href = lead.dataset.url || '/pages/contact';
      link.textContent = 'View This Lanyard';
    }

    if (reasons) {
      Object.keys(state).forEach((group) => {
        const item = document.createElement('li');
        item.textContent = REASONS[group] || 'Matches one selected choice.';
        reasons.appendChild(item);
      });
    }

    animateRecommendation(recommendation, options.direction || 0, rootState);
  }

  function stepRecommendation(root, direction) {
    const cards = Array.from(root.querySelectorAll(SELECTORS.card));
    const chips = Array.from(root.querySelectorAll(SELECTORS.chip));
    const state = getSelectedState(chips);
    const matches = cards.filter((card) => cardMatches(card, state));

    if (matches.length < 2) return;

    const rootState = getRootState(root);
    rootState.recommendationIndex = (rootState.recommendationIndex + direction + matches.length) % matches.length;
    updateRecommendation(root, cards, matches, state, { preserveIndex: true, direction });
  }

  function updateDisabledChips(cards, chips, state) {
    chips.forEach((chip) => {
      const group = chip.dataset.filterGroup;
      const value = normalize(chip.dataset.filterValue);
      const count = cards.filter((card) => {
        if (normalize(card.dataset[group]) !== value) return false;
        return cardMatches(card, state, group);
      }).length;

      chip.classList.toggle('is-disabled', count === 0 && !chip.classList.contains('is-selected'));
      chip.dataset.matchCount = String(count);
    });
  }

  function hideSingleValueGroups(root, cards) {
    const groups = root.querySelectorAll('[data-filter-group-wrap]');
    groups.forEach((wrap) => {
      const group = wrap.dataset.filterGroupWrap;
      const values = new Set(cards.map((card) => normalize(card.dataset[group])).filter(Boolean));
      wrap.classList.toggle('is-hidden', values.size <= 1);
    });
  }

  function initFilterGlow(root) {
    const panels = root.querySelectorAll('.ehua-guided-collection__filter-panel');
    panels.forEach((panel) => {
      ['border-glow', 'border-fill', 'edge-light'].forEach((layer) => {
        const className = `ehua-guided-collection__${layer}`;
        if (!panel.querySelector(`.${className}`)) {
          const node = document.createElement('span');
          node.className = className;
          node.setAttribute('aria-hidden', 'true');
          panel.prepend(node);
        }
      });

      const updateGlow = (event) => {
        const rect = panel.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const dx = x - centerX;
        const dy = y - centerY;
        const distanceX = centerX ? Math.abs(dx) / centerX : 0;
        const distanceY = centerY ? Math.abs(dy) / centerY : 0;
        const edge = Math.min(Math.max(Math.max(distanceX, distanceY) * 100, 0), 100);
        const glowOpacity = 0.18 + (Math.min(Math.max((edge - 34) / 66, 0), 1) * 0.82);
        let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;

        if (angle < 0) angle += 360;
        panel.style.setProperty('--ehua-border-edge', edge.toFixed(3));
        panel.style.setProperty('--ehua-border-glow', glowOpacity.toFixed(3));
        panel.style.setProperty('--ehua-border-angle', `${angle.toFixed(3)}deg`);
      };

      panel.addEventListener('pointermove', updateGlow);
      panel.addEventListener('pointerleave', () => {
        panel.style.setProperty('--ehua-border-edge', '0');
        panel.style.setProperty('--ehua-border-glow', '0.18');
      });
    });
  }

  function updateGridView(root) {
    const rootState = getRootState(root);
    const controls = Array.from(root.querySelectorAll(SELECTORS.gridView));
    const matchesOnly = rootState.viewMode === 'matches';

    root.classList.toggle('is-matches-only', matchesOnly);
    controls.forEach((control) => {
      const selected = control.dataset.ehuaGridView === rootState.viewMode;
      control.classList.toggle('is-selected', selected);
      control.setAttribute('aria-pressed', String(selected));
    });
  }

  function resetFilters(root, chips) {
    chips.forEach((chip) => {
      chip.classList.remove('is-selected');
      chip.setAttribute('aria-pressed', 'false');
    });

    getRootState(root).viewMode = 'context';
    update(root);
  }

  function update(root) {
    const cards = Array.from(root.querySelectorAll(SELECTORS.card));
    const chips = Array.from(root.querySelectorAll(SELECTORS.chip));
    const state = getSelectedState(chips);
    const selectedValues = Object.values(state);
    const matches = cards.filter((card) => cardMatches(card, state));

    cards.forEach((card) => {
      const match = matches.includes(card);
      card.classList.toggle('is-filtered-out', selectedValues.length > 0 && !match);
    });

    chips.forEach((chip) => {
      const isSelected = state[chip.dataset.filterGroup] === normalize(chip.dataset.filterValue);
      chip.classList.toggle('is-selected', isSelected);
      chip.setAttribute('aria-pressed', String(isSelected));
    });

    const summary = selectedValues.length ? selectedValues.join(' + ') : 'Start with one choice below.';
    setText(root.querySelector(SELECTORS.selectedSummary), summary);
    setText(root.querySelector(SELECTORS.matchCount), labelForCount(matches.length));
    setText(root.querySelector(SELECTORS.gridCount), labelForCount(matches.length));

    updateDisabledChips(cards, chips, state);
    updateGridView(root);
    updateRecommendation(root, cards, matches, state);
  }

  function init(root) {
    const cards = Array.from(root.querySelectorAll(SELECTORS.card));
    const chips = Array.from(root.querySelectorAll(SELECTORS.chip));
    const reset = root.querySelector(SELECTORS.reset);
    const gridReset = root.querySelector(SELECTORS.gridReset);
    const gridViews = Array.from(root.querySelectorAll(SELECTORS.gridView));
    const previous = root.querySelector(SELECTORS.recommendationPrevious);
    const next = root.querySelector(SELECTORS.recommendationNext);
    const viewMatches = root.querySelector(SELECTORS.viewMatches);

    if (!cards.length) return;

    hideSingleValueGroups(root, cards);
    initFilterGlow(root);

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const group = chip.dataset.filterGroup;
        const wasSelected = chip.classList.contains('is-selected');

        chips
          .filter((item) => item.dataset.filterGroup === group)
          .forEach((item) => {
            item.classList.remove('is-selected');
            item.setAttribute('aria-pressed', 'false');
          });

        if (!wasSelected) {
          chip.classList.add('is-selected');
          chip.setAttribute('aria-pressed', 'true');
        }

        update(root);
      });
    });

    if (reset) {
      reset.addEventListener('click', () => resetFilters(root, chips));
    }

    if (gridReset) {
      gridReset.addEventListener('click', () => resetFilters(root, chips));
    }

    gridViews.forEach((control) => {
      control.addEventListener('click', () => {
        getRootState(root).viewMode = control.dataset.ehuaGridView === 'matches' ? 'matches' : 'context';
        updateGridView(root);
      });
    });

    if (previous) {
      previous.addEventListener('click', () => stepRecommendation(root, -1));
    }

    if (next) {
      next.addEventListener('click', () => stepRecommendation(root, 1));
    }

    if (viewMatches) {
      viewMatches.addEventListener('click', (event) => {
        const target = root.querySelector('.ehua-guided-collection__products');
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
      });
    }

    update(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTORS.root).forEach(init);
  });
})();
